<template>
  <main id="main" class="main">
    <div class="pagetitle">
      <h1>WARMING SCRIPTS</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
          <router-link :to="{ name: 'warming-scripts' }" class="breadcrumb-item">Warming Scripts</router-link>
        </ol>
      </nav>
    </div>

    <section class="section">
      <div class="row"> 
        
        <!-- Scripts List -->
        <div class="col-lg-12">
          <div class="card card-chatroom">
            <div class="card-body">
              <h5 class="card-title card-title-chatroom">
                <i class="bi bi-file-text-fill me-2"></i>
                Scripts Management
              </h5>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <button
                    class="btn btn-sm btn-chatroom btn-primary-chatroom me-2"
                    @click="openCreateModal"
                    :disabled="loading"
                  >
                    <i class="bi bi-plus-circle"></i>
                    Create Script
                  </button>
                  <button
                    class="btn btn-sm btn-chatroom btn-secondary-chatroom"
                    @click="fetchScripts"
                    :disabled="loading"
                  >
                    <i class="bi bi-arrow-repeat"></i>
                  </button>
                </div>
                
                <div class="d-flex align-items-center gap-2">
                  <button
                    v-if="selectedScripts.length > 0"
                    class="btn btn-sm btn-danger"
                    @click="bulkDeleteScripts"
                  >
                    <i class="bi bi-trash"></i> Delete Selected ({{ selectedScripts.length }})
                  </button>
                  <div style="width: 250px;">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      style="height: 35px; font-size: 0.875rem;"
                      placeholder="Search..."
                      v-model="searchQuery"
                    >
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover shadow-sm table-bordered">
                  <thead class="table-primary">
                    <tr style="vertical-align:middle; text-align: center;">
                      <th style="width: 40px;">
                        <input type="checkbox" class="form-check-input" v-model="selectAll" @change="toggleSelectAll">
                      </th>
                      <th>No</th>
                      <th @click="sortBy('title')" style="cursor: pointer;">
                        Title <i :class="['bi', getSortIcon('title')]"></i>
                      </th>
                      <th @click="sortBy('category')" style="cursor: pointer;">
                        Category <i :class="['bi', getSortIcon('category')]"></i>
                      </th>
                      <th @click="sortBy('lines')" style="cursor: pointer;">
                        Lines <i :class="['bi', getSortIcon('lines')]"></i>
                      </th>
                      <th @click="sortBy('createdAt')" style="cursor: pointer;">
                        Created <i :class="['bi', getSortIcon('createdAt')]"></i>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="7" class="text-center">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-else
                      v-for="(script, index) in paginatedScripts"
                      :key="script.id"
                      style="vertical-align:middle;"
                    >
                      <td class="text-center">
                        <input 
                          type="checkbox" 
                          class="form-check-input" 
                          :value="script.id" 
                          v-model="selectedScripts"
                        >
                      </td>
                      <td class="text-center">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
                      <td>{{ script.title }}</td>
                      <td class="text-center">
                        <span class="badge bg-info">{{ script.category || 'N/A' }}</span>
                      </td>
                      <td class="text-center">{{ script.lineCount || 0 }}</td>
                      <td class="text-center">{{ formatDate(script.createdAt) }}</td>
                      <td class="text-center" nowrap>
                        <button
                          class="btn btn-sm btn-outline-primary me-1"
                          @click="viewLines(script)"
                          title="Lines"
                        >
                          <i class="bi bi-list-ul"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-info me-1"
                          @click="duplicateScript(script)"
                          title="Duplicate"
                        >
                          <i class="bi bi-files"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-warning me-1"
                          @click="editScript(script)"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteScript(script)"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!loading && paginatedScripts.length === 0">
                      <td colspan="7" class="text-center">No scripts found</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Pagination Controls -->
              <div class="d-flex justify-content-between align-items-center mt-3" v-if="!loading && sortedScripts.length > 0">
                <div class="d-flex align-items-center gap-2">
                  <label class="mb-0 small">Show:</label>
                  <select class="form-select form-select-sm" style="width: auto;" v-model.number="itemsPerPage" @change="changeItemsPerPage">
                    <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <span class="small text-muted">
                    Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, sortedScripts.length) }} of {{ sortedScripts.length }} scripts
                  </span>
                </div>
                
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="prevPage">
                        <i class="bi bi-chevron-left"></i>
                      </a>
                    </li>
                    
                    <li class="page-item" v-if="currentPage > 2">
                      <a class="page-link" href="#" @click.prevent="goToPage(1)">1</a>
                    </li>
                    <li class="page-item disabled" v-if="currentPage > 3">
                      <span class="page-link">...</span>
                    </li>
                    
                    <li class="page-item" v-if="currentPage > 1">
                      <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">{{ currentPage - 1 }}</a>
                    </li>
                    
                    <li class="page-item active">
                      <span class="page-link">{{ currentPage }}</span>
                    </li>
                    
                    <li class="page-item" v-if="currentPage < totalPages">
                      <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">{{ currentPage + 1 }}</a>
                    </li>
                    
                    <li class="page-item disabled" v-if="currentPage < totalPages - 2">
                      <span class="page-link">...</span>
                    </li>
                    <li class="page-item" v-if="currentPage < totalPages - 1">
                      <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">{{ totalPages }}</a>
                    </li>
                    
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="nextPage">
                        <i class="bi bi-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- Create/Edit Modal -->
    <div class="modal fade" id="scriptModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? 'Edit Script' : 'Create Script' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetForm"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveScript">
              <div class="mb-3">
                <label class="form-label">Title <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.title }"
                  v-model="form.title"
                  @input="clearError('title')"
                  placeholder="e.g., Casual Chat Script"
                >
                <div v-if="errors.title" class="invalid-feedback">
                  {{ errors.title }}
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.category }"
                  v-model="form.category"
                  @input="formatCategory"
                  placeholder="e.g., casual_chat, jual_beli_motor"
                >
                <div v-if="errors.category" class="invalid-feedback">
                  {{ errors.category }}
                </div>
                <small class="form-text text-muted">Use underscore (_) for spaces</small>
              </div>
              
              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea
                  class="form-control"
                  v-model="form.description"
                  rows="3"
                  placeholder="Optional description..."
                ></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-chatroom btn-secondary-chatroom" data-bs-dismiss="modal" @click="resetForm">Cancel</button>
            <button type="button" class="btn btn-sm btn-chatroom btn-primary-chatroom" @click="saveScript" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-save"></i> Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lines Management Modal - Clean Preview -->
    <div class="modal fade modal-smooth" id="linesModal" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header bg-primary text-white">
            <div>
              <h5 class="modal-title mb-0">
                <i class="bi bi-list-ul me-2"></i>{{ currentScript?.title }}
              </h5>
              <small class="opacity-75">
                {{ lines.length }} lines • {{ currentScript?.category || 'N/A' }}
              </small>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" @click="closeLinesModal"></button>
          </div>
          
          <!-- Body -->
          <div class="modal-body p-0">
            <div class="row g-0">
              <!-- Left: Preview -->
              <div class="col-lg-7 border-end">
                <div class="preview-panel">
                  <div class="preview-header">
                    <h6 class="mb-0">
                      <i class="bi bi-eye me-2"></i>Conversation Preview
                    </h6>
                    <button class="btn btn-sm btn-outline-secondary" @click="fetchLines" :disabled="loadingLines">
                      <i class="bi bi-arrow-repeat"></i>
                    </button>
                  </div>

                  <div class="preview-content">
                    <!-- Loading -->
                    <div v-if="loadingLines" class="text-center py-5">
                      <div class="spinner-border text-primary"></div>
                      <p class="text-muted mt-2">Loading...</p>
                    </div>

                    <!-- Messages -->
                    <div v-else-if="lines.length > 0" class="messages-list">
                      <div 
                        v-for="line in sortedLines" 
                        :key="'preview-' + line.id"
                        class="message-item"
                        :class="line.actorRole === 'ACTOR_A' ? 'actor-a' : 'actor-b'"
                      >
                        <div class="message-header">
                          <span class="seq-badge">#{{ line.sequenceOrder }}</span>
                          <span class="actor-badge">
                            {{ line.actorRole === 'ACTOR_A' ? 'Actor A' : 'Actor B' }}
                          </span>
                          <span class="typing-badge">
                            <i class="bi bi-stopwatch"></i> {{ line.typingDurationSec }}s
                          </span>
                        </div>
                        <div class="message-content">
                          {{ renderMessagePreview(line.messageContent) }}
                        </div>
                      </div>
                    </div>

                    <!-- Empty -->
                    <div v-else class="empty-state">
                      <i class="bi bi-inbox"></i>
                      <p>No lines yet</p>
                      <small class="text-muted">Click "Add Line" to start</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Management -->
              <div class="col-lg-5">
                <div class="management-panel">
                  <div class="management-header">
                    <h6 class="mb-3">
                      <i class="bi bi-gear me-2"></i>Line Management
                    </h6>
                    <div class="d-flex gap-2 mb-3">
                      <button class="btn btn-sm btn-primary flex-fill" @click="openAddLineModal">
                        <i class="bi bi-plus-circle"></i> Add
                      </button>
                      <button class="btn btn-sm btn-success flex-fill" @click="openGenerateModal">
                        <i class="bi bi-magic"></i> Generate
                      </button>
                    </div>
                  </div>

                  <div class="lines-list">
                    <div v-if="loadingLines" class="text-center py-4">
                      <div class="spinner-border spinner-border-sm"></div>
                    </div>

                    <div v-else-if="lines.length === 0" class="text-center py-4 text-muted">
                      <i class="bi bi-inbox" style="font-size: 2rem;"></i>
                      <p class="small mb-0 mt-2">No lines</p>
                    </div>

                    <div v-else>
                      <draggable 
                        v-model="lines" 
                        @end="handleReorder"
                        handle=".drag-handle"
                        item-key="id"
                      >
                        <template #item="{element: line}">
                          <div class="line-card">
                            <div class="line-card-header">
                              <div class="d-flex align-items-center gap-2">
                                <i class="bi bi-grip-vertical drag-handle" style="cursor: move; color: #6c757d;" title="Drag to reorder"></i>
                                <span class="badge bg-secondary">{{ line.sequenceOrder }}</span>
                                <span class="badge" :class="line.actorRole === 'ACTOR_A' ? 'bg-primary' : 'bg-success'">
                                  {{ line.actorRole === 'ACTOR_A' ? 'A' : 'B' }}
                                </span>
                                <small class="text-muted">{{ line.typingDurationSec }}s</small>
                              </div>
                              <div class="d-flex gap-1">
                                <button class="btn btn-sm btn-outline-warning" @click="editLine(line)">
                                  <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" @click="deleteLine(line)">
                                  <i class="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>
                            <div class="line-card-body">
                              <small class="text-muted">Raw:</small>
                              <div class="raw-message">{{ line.messageContent }}</div>
                              <small class="text-muted">Preview:</small>
                              <div class="preview-message">
                                {{ renderMessagePreview(line.messageContent) }}
                              </div>
                            </div>
                          </div>
                        </template>
                      </draggable>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Line Modal -->
    <div class="modal fade" id="lineFormModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ lineEditMode ? 'Edit Line' : 'Add Line' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetLineForm"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveLine">
              <div class="mb-3">
                <label class="form-label">Sequence Order <span class="text-danger">*</span></label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="lineForm.sequenceOrder"
                  :class="{ 'is-invalid': lineErrors.sequenceOrder }"
                  @input="clearLineError('sequenceOrder')"
                  min="1"
                >
                <div v-if="lineErrors.sequenceOrder" class="invalid-feedback">
                  {{ lineErrors.sequenceOrder }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Actor Role <span class="text-danger">*</span></label>
                <select
                  class="form-select form-select-sm"
                  v-model="lineForm.actorRole"
                  :class="{ 'is-invalid': lineErrors.actorRole }"
                  @change="clearLineError('actorRole')"
                >
                  <option value="">-- Select Actor --</option>
                  <option value="ACTOR_A">ACTOR_A (Sender)</option>
                  <option value="ACTOR_B">ACTOR_B (Receiver)</option>
                </select>
                <div v-if="lineErrors.actorRole" class="invalid-feedback">
                  {{ lineErrors.actorRole }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Message Content <span class="text-danger">*</span></label>
                <textarea
                  class="form-control form-control-sm"
                  v-model="lineForm.messageContent"
                  :class="{ 'is-invalid': lineErrors.messageContent }"
                  @input="clearLineError('messageContent')"
                  rows="4"
                  placeholder="Type message here... MUST use {spintax|options}"
                ></textarea>
                <div v-if="lineErrors.messageContent" class="invalid-feedback">
                  {{ lineErrors.messageContent }}
                </div>
                <div class="alert alert-warning py-1 px-2 mt-2 mb-0">
                  <small>
                    <i class="bi bi-exclamation-triangle me-1"></i>
                    <strong>WAJIB gunakan spintax:</strong> {Hello|Hi|Hey} dan variabel: {TIME_GREETING}, akan di render otomatis oleh sistem saat pengiriman, agar memiliki variasi yang berbeda
                  </small>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Typing Duration: <strong>{{ lineForm.typingDurationSec }} seconds</strong>
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="range"
                  class="form-range"
                  v-model.number="lineForm.typingDurationSec"
                  min="1"
                  max="5"
                  step="1"
                >
                <div class="d-flex justify-content-between">
                  <small class="text-muted">1s (Fast)</small>
                  <small class="text-muted">5s (Slow)</small>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-chatroom btn-secondary-chatroom" data-bs-dismiss="modal" @click="resetLineForm">Cancel</button>
            <button type="button" class="btn btn-sm btn-chatroom btn-primary-chatroom" @click="saveLine" :disabled="loadingLines">
              <span v-if="loadingLines" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-save"></i> Save
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Generate Lines Modal -->
    <div class="modal fade" id="generateModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Generate Script Lines</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetGenerateForm"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="generateLines">
              <div class="mb-3">
                <label class="form-label">Number of Lines <span class="text-danger">*</span></label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="generateForm.lineCount"
                  :class="{ 'is-invalid': generateErrors.lineCount }"
                  @input="clearGenerateError('lineCount')"
                  min="1"
                  max="50"
                  placeholder="e.g., 10"
                >
                <div v-if="generateErrors.lineCount" class="invalid-feedback">
                  {{ generateErrors.lineCount }}
                </div>
                <small class="text-muted">Generate 1-50 lines at once</small>
              </div>

              <div class="mb-3">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <select
                  class="form-select form-select-sm"
                  v-model="generateForm.category"
                  :class="{ 'is-invalid': generateErrors.category }"
                  @change="clearGenerateError('category')"
                >
                  <option value="">-- Select Category --</option>
                  <option v-for="cat in availableCategories" :key="cat" :value="cat">
                    {{ cat }}
                  </option>
                </select>
                <div v-if="generateErrors.category" class="invalid-feedback">
                  {{ generateErrors.category }}
                </div>
                <small class="text-muted">Category from available templates</small>
              </div>

              <div class="alert alert-info py-2 px-3 mb-0">
                <small>
                  <i class="bi bi-info-circle me-1"></i>
                  <strong>Auto-generate:</strong> Sistem akan membuat {{ generateForm.lineCount || 0 }} baris percakapan 
                  dengan kategori "{{ generateForm.category || '...' }}" secara otomatis dengan spintax dan variabel.
                </small>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-chatroom btn-secondary-chatroom" data-bs-dismiss="modal" @click="resetGenerateForm">Cancel</button>
            <button type="button" class="btn btn-sm btn-chatroom btn-success" @click="generateLines" :disabled="loadingGenerate">
              <span v-if="loadingGenerate" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-magic"></i> Generate
            </button>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';

export default {
  components: {
    draggable
  },
  data() {
    return {
      scripts: [],
      loading: false,
      searchQuery: '',
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
      
      editMode: false,
      currentScript: null,
      form: {
        title: '',
        category: '',
        description: ''
      },
      errors: {
        title: '',
        category: ''
      },
      
      // Sorting
      sortColumn: 'createdAt',
      sortDirection: 'desc',
      
      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
      perPageOptions: [10, 25, 50],
      
      // Bulk Delete
      selectedScripts: [],
      selectAll: false,
      
      // Lines management
      lines: [],
      loadingLines: false,
      lineEditMode: false,
      currentLine: null,
      lineForm: {
        sequenceOrder: 1,
        actorRole: '',
        messageContent: '',
        typingDurationSec: 3
      },
      lineErrors: {
        sequenceOrder: '',
        actorRole: '',
        messageContent: '',
        typingDurationSec: ''
      },
      
      // Generate lines
      loadingGenerate: false,
      availableCategories: [],
      generateForm: {
        lineCount: 10,
        category: ''
      },
      generateErrors: {
        lineCount: '',
        category: ''
      }
    };
  },
  
  computed: {
    filteredScripts() {
      if (!this.searchQuery) return this.scripts;
      const q = this.searchQuery.toLowerCase();
      return this.scripts.filter(s => 
        s.title?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q)
      );
    },
    
    sortedScripts() {
      const sorted = [...this.filteredScripts];
      sorted.sort((a, b) => {
        let aVal = a[this.sortColumn];
        let bVal = b[this.sortColumn];
        
        if (this.sortColumn === 'lines') {
          aVal = a.lineCount || 0;
          bVal = b.lineCount || 0;
        }
        
        if (this.sortColumn === 'createdAt' || this.sortColumn === 'updatedAt') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    
    paginatedScripts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedScripts.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.sortedScripts.length / this.itemsPerPage);
    },
    
    isAllSelected() {
      return this.paginatedScripts.length > 0 && 
             this.paginatedScripts.every(s => this.selectedScripts.includes(s.id));
    },
    
    sortedLines() {
      return [...this.lines].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    }
  },
  
  mounted() {
    this.fetchScripts();
  },
  
  methods: {
    async fetchScripts() {
      this.loading = true;
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/scripts`);
        const scriptsData = res.data.data.scripts || [];
        
        // Fetch line count for each script
        this.scripts = await Promise.all(scriptsData.map(async (script) => {
          try {
            const linesRes = await axios.get(`${this.baseUrl}/api/warming/scripts/${script.id}/lines`);
            return {
              ...script,
              lineCount: linesRes.data.data.total || 0
            };
          } catch (error) {
            return {
              ...script,
              lineCount: 0
            };
          }
        }));
      } catch (error) {
        console.error('Failed to fetch scripts:', error);
        this.showToast('Failed to load scripts', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    openCreateModal() {
      this.editMode = false;
      this.currentScript = null;
      this.resetForm();
      const modal = new bootstrap.Modal(document.getElementById('scriptModal'));
      modal.show();
    },
    
    editScript(script) {
      this.editMode = true;
      this.currentScript = script;
      this.form = {
        title: script.title,
        category: script.category || '',
        description: script.description || ''
      };
      this.errors = { title: '', category: '' };
      const modal = new bootstrap.Modal(document.getElementById('scriptModal'));
      modal.show();
    },
    
    validateForm() {
      this.errors = { title: '', category: '' };
      let isValid = true;
      
      if (!this.form.title || this.form.title.trim() === '') {
        this.errors.title = 'Title is required';
        isValid = false;
      }
      
      if (!this.form.category || this.form.category.trim() === '') {
        this.errors.category = 'Category is required';
        isValid = false;
      } else if (this.form.category.includes(' ')) {
        this.errors.category = 'Category cannot contain spaces. Use underscore (_)';
        isValid = false;
      } else if (!/^[a-z0-9_]+$/.test(this.form.category)) {
        this.errors.category = 'Category must be lowercase letters, numbers, and underscores only';
        isValid = false;
      }
      
      return isValid;
    },
    
    formatCategory() {
      // Auto-replace spaces with underscores and convert to lowercase
      this.form.category = this.form.category
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      this.clearError('category');
    },
    
    clearError(field) {
      this.errors[field] = '';
    },
    
    resetForm() {
      this.form = { title: '', category: '', description: '' };
      this.errors = { title: '', category: '' };
    },
    
    async saveScript() {
      if (!this.validateForm()) {
        return;
      }
      
      this.loading = true;
      try {
        if (this.editMode) {
          await axios.put(`${this.baseUrl}/api/warming/scripts/${this.currentScript.id}`, this.form);
          this.showToast('Script updated successfully!', 'success');
        } else {
          await axios.post(`${this.baseUrl}/api/warming/scripts`, this.form);
          this.showToast('Script created successfully!', 'success');
        }
        
        bootstrap.Modal.getInstance(document.getElementById('scriptModal')).hide();
        this.fetchScripts();
        this.resetForm();
      } catch (error) {
        console.error('Save error:', error);
        const message = error.response?.data?.message || 'Failed to save script';
        this.showToast(message, 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async deleteScript(script) {
      const result = await this.$swal.fire({
        title: 'Delete Script?',
        text: `Delete "${script.title}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await axios.delete(`${this.baseUrl}/api/warming/scripts/${script.id}`);
        this.showToast('Script deleted successfully!', 'success');
        this.fetchScripts();
      } catch (error) {
        console.error('Delete script error:', error);
        
        // Check if it's a foreign key constraint error
        const errorMessage = error.response?.data?.message || error.message || '';
        const errorDetails = error.response?.data?.details || '';
        
        if (errorMessage.includes('foreign key') || 
            errorMessage.includes('RESTRICT') || 
            errorDetails.includes('fk_rooms_script') ||
            errorDetails.includes('warming_rooms')) {
          this.$swal.fire({
            title: 'Cannot Delete Script',
            html: `
              <p>This script is currently being used by one or more <strong>Rooms</strong>.</p>
              <p class="text-muted small mt-2">Please remove this script from all rooms first, or delete those rooms before deleting this script.</p>
            `,
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        } else {
          this.showToast('Failed to delete script', 'error');
        }
      }
    },
    
    // Sorting Methods
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },
    
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'bi-arrow-down-up';
      return this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
    },
    
    // Pagination Methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    changeItemsPerPage() {
      this.currentPage = 1;
    },
    
    // Duplicate & Bulk Delete Methods
    async duplicateScript(script) {
      try {
        const payload = {
          title: `${script.title} (Copy)`,
          category: script.category,
          description: script.description
        };
        
        await axios.post(`${this.baseUrl}/api/warming/scripts`, payload);
        this.showToast('Script duplicated!', 'success');
        this.fetchScripts();
      } catch (error) {
        this.showToast('Failed to duplicate script', 'error');
      }
    },
    
    async bulkDeleteScripts() {
      if (this.selectedScripts.length === 0) return;
      
      const result = await this.$swal.fire({
        title: 'Delete Scripts?',
        text: `Delete ${this.selectedScripts.length} selected script(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete all'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await Promise.all(
          this.selectedScripts.map(id => 
            axios.delete(`${this.baseUrl}/api/warming/scripts/${id}`)
          )
        );
        
        this.showToast(`${this.selectedScripts.length} script(s) deleted`, 'success');
        this.selectedScripts = [];
        this.selectAll = false;
        this.fetchScripts();
      } catch (error) {
        this.showToast('Failed to delete some scripts', 'error');
      }
    },
    
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedScripts = this.paginatedScripts.map(s => s.id);
      } else {
        this.selectedScripts = [];
      }
    },
    
    
    viewLines(script) {
      this.currentScript = script;
      this.lines = [];
      const modal = new bootstrap.Modal(document.getElementById('linesModal'));
      modal.show();
      this.fetchLines();
    },
    
    async fetchLines() {
      if (!this.currentScript) return;
      
      this.loadingLines = true;
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines`);
        this.lines = res.data.data.lines || [];
      } catch (error) {
        console.error('Failed to fetch lines:', error);
        this.showToast('Failed to load lines', 'error');
      } finally {
        this.loadingLines = false;
      }
    },
    
    closeLinesModal() {
      this.lines = [];
      this.currentScript = null;
    },
    
    openAddLineModal() {
      this.lineEditMode = false;
      this.currentLine = null;
      this.resetLineForm();
      // Auto-set next sequence
      if (this.lines.length > 0) {
        const maxSeq = Math.max(...this.lines.map(l => l.sequenceOrder));
        this.lineForm.sequenceOrder = maxSeq + 1;
      }
      const modal = new bootstrap.Modal(document.getElementById('lineFormModal'));
      modal.show();
    },
    
    editLine(line) {
      this.lineEditMode = true;
      this.currentLine = line;
      this.lineForm = {
        sequenceOrder: line.sequenceOrder,
        actorRole: line.actorRole,
        messageContent: line.messageContent,
        typingDurationSec: line.typingDurationSec
      };
      this.lineErrors = { sequenceOrder: '', actorRole: '', messageContent: '', typingDurationSec: '' };
      const modal = new bootstrap.Modal(document.getElementById('lineFormModal'));
      modal.show();
    },
    
    validateLineForm() {
      this.lineErrors = { sequenceOrder: '', actorRole: '', messageContent: '', typingDurationSec: '' };
      let isValid = true;
      
      if (!this.lineForm.sequenceOrder || this.lineForm.sequenceOrder < 1) {
        this.lineErrors.sequenceOrder = 'Sequence order must be at least 1';
        isValid = false;
      }
      
      if (!this.lineForm.actorRole) {
        this.lineErrors.actorRole = 'Actor role is required';
        isValid = false;
      }
      
      if (!this.lineForm.messageContent || this.lineForm.messageContent.trim() === '') {
        this.lineErrors.messageContent = 'Message content is required';
        isValid = false;
      }
      
      if (!this.lineForm.typingDurationSec || this.lineForm.typingDurationSec < 1 || this.lineForm.typingDurationSec > 5) {
        this.lineErrors.typingDurationSec = 'Typing duration must be between 1-5 seconds';
        isValid = false;
      }
      
      return isValid;
    },
    
    clearLineError(field) {
      this.lineErrors[field] = '';
    },
    
    resetLineForm() {
      this.lineForm = {
        sequenceOrder: 1,
        actorRole: '',
        messageContent: '',
        typingDurationSec: 3
      };
      this.lineErrors = { sequenceOrder: '', actorRole: '', messageContent: '', typingDurationSec: '' };
    },
    
    async saveLine() {
      if (!this.validateLineForm()) {
        return;
      }
      
      this.loadingLines = true;
      try {
        if (this.lineEditMode) {
          await axios.put(
            `${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines/${this.currentLine.id}`,
            this.lineForm
          );
          this.showToast('Line updated successfully!', 'success');
        } else {
          await axios.post(
            `${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines`,
            this.lineForm
          );
          this.showToast('Line added successfully!', 'success');
        }
        
        bootstrap.Modal.getInstance(document.getElementById('lineFormModal')).hide();
        this.fetchLines();
        this.resetLineForm();
      } catch (error) {
        console.error('Save line error:', error);
        const message = error.response?.data?.message || 'Failed to save line';
        this.showToast(message, 'error');
      } finally {
        this.loadingLines = false;
      }
    },
    
    async deleteLine(line) {
      const result = await this.$swal.fire({
        title: 'Delete Line?',
        text: `Delete line #${line.sequenceOrder}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, delete'
      });
      
      if (!result.isConfirmed) return;
      
      try {
        await axios.delete(
          `${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines/${line.id}`
        );
        this.showToast('Line deleted successfully!', 'success');
        this.fetchLines();
      } catch (error) {
        this.showToast('Failed to delete line', 'error');
      }
    },
    
    async handleReorder() {
      // Update sequenceOrder for all lines based on new position
      this.lines.forEach((line, index) => {
        line.sequenceOrder = index + 1;
      });
      
      try {
        // Send updated order to backend
        const payload = this.lines.map(line => ({
          id: line.id,
          sequenceOrder: line.sequenceOrder
        }));
        
        await axios.put(
          `${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines/reorder`,
          { lines: payload }
        );
        
        this.showToast('Lines reordered successfully!', 'success');
      } catch (error) {
        console.error('Reorder error:', error);
        this.showToast('Failed to save new order', 'error');
        // Refresh to get correct order from server
        this.fetchLines();
      }
    },
    
    async openGenerateModal() {
      this.resetGenerateForm();
      await this.fetchTemplateCategories();
      const modal = new bootstrap.Modal(document.getElementById('generateModal'));
      modal.show();
    },
    
    async fetchTemplateCategories() {
      try {
        const res = await axios.get(`${this.baseUrl}/api/warming/templates`);
        const templates = res.data.data.templates || [];
        // Get unique categories
        this.availableCategories = [...new Set(templates.map(t => t.category))].filter(Boolean);
      } catch (error) {
        console.error('Failed to fetch template categories:', error);
        this.availableCategories = [];
      }
    },
    
    validateGenerateForm() {
      this.generateErrors = { lineCount: '', category: '' };
      let isValid = true;
      
      if (!this.generateForm.lineCount || this.generateForm.lineCount < 1 || this.generateForm.lineCount > 50) {
        this.generateErrors.lineCount = 'Line count must be between 1-50';
        isValid = false;
      }
      
      if (!this.generateForm.category || this.generateForm.category.trim() === '') {
        this.generateErrors.category = 'Category is required';
        isValid = false;
      }
      
      return isValid;
    },
    
    clearGenerateError(field) {
      this.generateErrors[field] = '';
    },
    
    resetGenerateForm() {
      this.generateForm = {
        lineCount: 10,
        category: ''
      };
      this.generateErrors = { lineCount: '', category: '' };
    },
    
    async generateLines() {
      if (!this.validateGenerateForm()) {
        return;
      }
      
      this.loadingGenerate = true;
      try {
        await axios.post(
          `${this.baseUrl}/api/warming/scripts/${this.currentScript.id}/lines/generate`,
          this.generateForm
        );
        
        this.showToast(`Successfully generated ${this.generateForm.lineCount} lines!`, 'success');
        bootstrap.Modal.getInstance(document.getElementById('generateModal')).hide();
        this.fetchLines();
        this.resetGenerateForm();
      } catch (error) {
        console.error('Generate lines error:', error);
        const message = error.response?.data?.message || 'Failed to generate lines';
        this.showToast(message, 'error');
      } finally {
        this.loadingGenerate = false;
      }
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('id-ID');
    },
    
    formatDateForChat(date) {
      const now = new Date();
      const inputDate = new Date(date);
      
      if (inputDate.toDateString() === now.toDateString()) {
        return 'Today';
      }
      
      return inputDate.toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    },
    
    renderMessagePreview(messageContent) {
      if (!messageContent) return '(empty)';
      
      let text = messageContent;
      
      // Replace variables
      const now = new Date();
      const hour = now.getHours();
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const dayName = dayNames[now.getDay()];
      const date = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      
      let timeGreeting = 'Selamat Pagi';
      if (hour >= 11 && hour < 15) timeGreeting = 'Selamat Siang';
      else if (hour >= 15 && hour < 18) timeGreeting = 'Selamat Sore';
      else if (hour >= 18 || hour < 4) timeGreeting = 'Selamat Malam';
      
      text = text.replace(/{TIME_GREETING}/g, timeGreeting);
      text = text.replace(/{DAY_NAME}/g, dayName);
      text = text.replace(/{DATE}/g, date);
      
      // Render spintax - pick random option
      text = text.replace(/{([^}]+)}/g, (match, content) => {
        const options = content.split('|');
        return options[Math.floor(Math.random() * options.length)];
      });
      
      return text;
    },
    
    getCurrentTimeGreeting() {
      const hour = new Date().getHours();
      if (hour >= 11 && hour < 15) return 'Selamat Siang';
      if (hour >= 15 && hour < 18) return 'Selamat Sore';
      if (hour >= 18 || hour < 4) return 'Selamat Malam';
      return 'Selamat Pagi';
    },
    
    getCurrentDayName() {
      const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      return dayNames[new Date().getDay()];
    },
    
    getCurrentDate() {
      return new Date().toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    },
    
    getRandomTime() {
      const hour = Math.floor(Math.random() * 12) + 8;
      const minute = Math.floor(Math.random() * 60);
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    },
    
    showToast(message, type = 'info') {
      this.Toasttt(message, type);
    },

    Toasttt(msg, type, detail){
      const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          background: "#F8F8F8",
          didOpen: (toast) => {
              toast.onmouseenter = this.$swal.stopTimer;
              toast.onmouseleave = this.$swal.resumeTimer;
          }
      });
          Toast.fire({
          icon: type,
          title: msg,
          text: detail,
      });
    },

  }
};
</script>

